import { useLayoutEffect, useRef, useState } from "react";

/** Multiplier applied to the font size to get the line box; also used by the caller. */
export const FIT_TEXT_LINE_HEIGHT = 1.2;

/**
 * Picks the largest font size at which text fits a fixed box, wrapping if that buys
 * a bigger size than a single line would.
 *
 * Built for the navbar clinic name, where neither truncation nor a taller navbar is
 * acceptable: the clinic name is how a vet confirms which practice they are charting
 * into, so a cut-off name is worse than a small or two-line one. Real practice names
 * run long enough to need most of a laptop navbar at the 20px base.
 *
 * The height budget is free space: the navbar is 64px tall whatever the name does,
 * while a one line name only uses ~24px of it. Wrapping into that headroom roughly
 * doubles the width the text can occupy, which buys back several font sizes, and the
 * navbar does not grow as long as the result stays within `maxHeight`.
 *
 * `maxLines` bounds that wrapping independently of the height budget. Three or more
 * lines of a clinic name in a navbar reads as a paragraph rather than a heading, so
 * the cap is the binding constraint at small sizes where the height alone would allow
 * four. The returned `maxHeight` honours it too, so the overflow case cannot show a
 * third line either.
 *
 * Returns a ref for the element, the size to render at, a hard `maxHeight`, and `fits`.
 * `floorPx` is a legibility threshold: when nothing down to it fits, `fits` is false and
 * the caller should hide the text rather than show it clipped or unreadably small. That
 * is width-driven in practice, so a short name can still show where a long one cannot.
 */
export const useFitText = (text: string, basePx: number, floorPx: number, maxLines = 2) => {
    const ref = useRef<HTMLHeadingElement>(null);
    const [fontSize, setFontSize] = useState(basePx);
    const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);
    // Whether the text fits at a size still worth reading. False means the caller
    // should stop showing it rather than render something illegible or clipped.
    const [fits, setFits] = useState(true);

    useLayoutEffect(() => {
        const el = ref.current;
        const parent = el?.parentElement;
        if (!el || !parent) return;

        const measure = () => {
            const siblings = Array.from(parent.children).filter((c) => c !== el);
            const takenBySiblings = siblings.reduce((sum, c) => sum + c.getBoundingClientRect().width, 0);
            const gap = parseFloat(getComputedStyle(parent).columnGap) || 0;
            const width = parent.clientWidth - takenBySiblings - gap * siblings.length;
            // The tallest sibling already sets the row height, so anything up to it is free.
            const heightBudget = siblings.reduce(
                (tallest, c) => Math.max(tallest, c.getBoundingClientRect().height),
                0,
            );
            if (width <= 0 || heightBudget <= 0) return;

            // The usable height is whichever runs out first: the row's free space, or
            // `maxLines` worth of line boxes at the size being tried.
            const allowedHeight = (size: number) =>
                Math.min(heightBudget, Math.ceil(maxLines * FIT_TEXT_LINE_HEIGHT * size));

            // One offscreen probe, resized per candidate. Measuring the live element
            // instead would make each result depend on the size already applied to it.
            const probe = document.createElement("div");
            probe.textContent = text;
            probe.style.cssText =
                `position:absolute;visibility:hidden;top:0;left:-9999px;` +
                `width:${width}px;white-space:normal;overflow-wrap:break-word;` +
                `line-height:${FIT_TEXT_LINE_HEIGHT};font-weight:${getComputedStyle(el).fontWeight};` +
                `font-family:${getComputedStyle(el).fontFamily};`;
            document.body.appendChild(probe);

            const fitsAt = (size: number) => {
                probe.style.fontSize = `${size}px`;
                return probe.scrollHeight <= allowedHeight(size) && probe.scrollWidth <= width + 1;
            };

            let best: number | null = null;
            let lo = floorPx;
            let hi = basePx;
            while (lo <= hi) {
                const mid = Math.floor((lo + hi) / 2);
                if (fitsAt(mid)) {
                    best = mid;
                    lo = mid + 1;
                } else {
                    hi = mid - 1;
                }
            }

            document.body.removeChild(probe);
            const chosen = best ?? floorPx;
            setFontSize(chosen);
            setFits(best !== null);
            // Hard cap, not just a fitting target. When even `floorPx` overflows (a phone,
            // where the logo and tabs leave the name almost no width) the text would
            // otherwise wrap to whatever height it needed and become the tallest item in
            // the row, growing the navbar. Capping it clips the overflow instead, and
            // keeps it to `maxLines` on the way.
            setMaxHeight(allowedHeight(chosen));
        };

        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(parent);
        // The siblings too, not just the parent. What this measures is the space the siblings
        // leave, but the parent is `flex-1 min-w-0` so the row sets its width: a sibling can
        // change size with the parent's own box unmoved, and nothing would fire. Both of the
        // navbar's siblings do exactly that - the logo has no intrinsic width until its image
        // decodes, and the tab strip mounts with the route. Left on the parent alone, a cold
        // load sizes the name against space the logo had not claimed yet, and because `fits`
        // stayed true it is clipped rather than dropped: a half clinic name, which is the one
        // outcome this hook exists to prevent. It held that way until the window was resized.
        for (const sibling of Array.from(parent.children)) observer.observe(sibling);

        // Fraunces arrives over the network with `display: swap`, so the first measurement can
        // land against the fallback serif - which is ~12% narrower at this weight and size, so
        // the name is sized to fit a box the real font then overflows. No resize follows a font
        // swap, and the navbar's right-hand cluster is all icons, so nothing the observer
        // watches changes width either. Re-measure when the fonts settle.
        let cancelled = false;
        document.fonts?.ready.then(() => {
            if (!cancelled) measure();
        });

        return () => {
            cancelled = true;
            observer.disconnect();
        };
    }, [text, basePx, floorPx, maxLines]);

    return { ref, fontSize, maxHeight, fits };
};
